import { useEffect, useMemo, useState } from 'react';
import type { Ref } from 'react';
import { PhotoIcon } from '@heroicons/react/24/outline';
import {
  reportLayoutOf, REPORT_FONTS, SECTION_UNDERLINE_WIDTHS, SURFACE_RADII,
  LETTERHEAD_LAYOUTS, LETTERHEAD_CONFIDENTIALITY_LEVELS, LETTERHEAD_CONFIDENTIALITY_LABELS,
  MEDIA_SUMMARY_LAYOUTS, MEDIA_SUMMARY_IMAGE_FITS, MEDIA_SUMMARY_GROUP_BY_VALUES, UNREALIZED_MEDIA_SUMMARY_GROUP_BY,
  // Story 4-120 — Style Tools' allowlisted-token arrays, same "iterate the
  // curated list so a future additive entry can't render label-less"
  // pattern every existing Style-tab control above already uses.
  TYPOGRAPHY_CAPTION_SCALES, COLOR_ROLE_BRAND_VALUES, COLOR_ROLE_HEADING_VALUES, COLOR_ROLE_BORDER_VALUES,
  COLOR_ROLE_FINDING_SEVERITY_VALUES, BORDER_DENSITIES, COVER_VARIANTS, LETTERHEAD_VARIANTS, TABLE_VARIANTS,
  FINDING_VARIANTS,
} from '../../lib/agentShared/formSchema';
import type {
  FormSchemaV2, LetterheadSlotKey, MarginsPreset, PageOrientation, ReportContentToggles, ReportFont, ReportLayout,
  SectionUnderlineWidth, SurfaceRadius, LetterheadLayout, LetterheadConfidentiality, FindingsGroupBy, ShowEmptyMode,
  MediaSummaryLayout, MediaSummaryImageFit, MediaSummaryGroupBy,
  TypographyCaptionScale, ColorRoleBrand, ColorRoleHeading, ColorRoleBorder, ColorRoleFindingSeverity,
  BorderDensity, CoverVariant, LetterheadVariant, TableVariant, FindingVariant,
} from '../../lib/agentShared/formSchema';
import { MARGIN_PRESETS, PAGE_ORIENTATIONS, marginsPresetIsSafe } from '../../report-document';
import { listReportStyles, saveReportStyle } from '../../lib/agentClient';
import type { SavedStyle } from '../../lib/agentClient';
import { buildPreviewReportContent } from './reportPreviewContent';
import type { ReportStudioCommand } from './reportStudio/reportStudioCommands';
import type { ReportPresentation } from './reportStudio/reportPresentationSchema';
// Story 4-120 — "Organization brand preset": reads the org's ALREADY-
// CONFIGURED branding (`OrgBrandingPage.tsx`'s own mechanism, story 6-8/6-9)
// as a one-click starting point for the report's accent/logo/font — not a
// second, parallel brand-config system. Read-only here (`org:branding:read`,
// gated the same way that page's own editor is), never writes org branding.
import { getOrgBranding, orgBrandingKeys } from '../../lib/api/orgBranding.gen';
import { useAuth } from '../../lib/auth';
import { useCapabilities } from '../../lib/capabilities';
import { useQuery } from '@tanstack/react-query';
// The reports module's public export (story 4-78) — the SAME component
// review/report-detail render through. Barrel import, not a reach into
// `modules/reports/components/*`: this file is shell code (not itself a
// module), and `no-restricted-imports` (P18 §1) blocks the deeper path.
import { ReportRender, PagedReportPreview } from '../../modules/reports';
import type { ReportPrintHostHandle } from '../../modules/reports';
// Story 4-82's cover-page control uses the real `Switch` primitive
// (`role="switch"`, real `<button type="button">`) instead of copying this
// file's pre-existing raw `<input type="checkbox">` pattern (`toggleRow`,
// below) — a11y-correct and already in the design system, just not
// previously used here. Story 4-84's font control (below) switches to the
// same DS `RadioGroup` primitive for the same reason — it already existed,
// already a11y-passed and galleried, just not previously used here either.
import {
  Callout,
  ContextHeader,
  Cluster,
  ColorInput,
  DialogActions,
  DirectionalPad,
  FileButton,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  RadioGroup,
  ReportLogoImage,
  SegmentedControl,
  Select,
  SlotGridPicker,
  Stack,
  Surface,
  Switch,
  Textarea,
  ToolbarActions,
  TokenBox,
  findAdjacentSlot,
  reportPageWidthMm,
} from '../ui';
import type { DirectionalPadDirection } from '../ui';
// Story 4-88b — the letterhead grid's topology + relocate logic, shared
// verbatim with the `/app/design` gallery demo (`letterheadGrid.ts`'s own
// header comment) so the real feature and the gallery can never drift onto
// two different grids.
import {
  LETTERHEAD_GRID_CELLS, LETTERHEAD_GRID_ROWS, LETTERHEAD_GRID_COLS, LETTERHEAD_SLOT_LABELS, relocateLetterheadSlot,
} from './letterheadGrid';

// "Edit report" — the Report Studio inspector's document-level panel
// (mounted by `ReportStudioInspector` when nothing more specific is
// selected) places Style/Content tools here, and the production report
// renderer in the center canvas. Layout lives in schema.report, so the
// agent's form_set_report_layout tool drives exactly the same state.
//
// Story 4-113 — every control below used to call `onChange(nextSchema)`
// directly (a hand-built object spread per call site). It now builds a
// `ReportStudioCommand` and calls `dispatch` instead — the SAME typed-
// command path `useReportStudio.ts` exposes, so a Style/Content edit is
// undo-tracked through that hook's own stack rather than mutating
// `schema.report` inline. See that hook's header comment for the full
// architecture; this component does not know the stack exists, it only
// knows how to build commands.
interface ReportDesignerProps {
  schema: FormSchemaV2;
  dispatch: (command: ReportStudioCommand) => void;
}

export interface ReportDesignerSettingsProps extends ReportDesignerProps {
  // Story 4-114 — 'layout' is the Document Tools tab (page format,
  // orientation, margin preset, cover page, table of contents). Story 4-115
  // — 'headerFooter' is the Header/Footer Tools tab (letterhead slots,
  // layout density, confidentiality, text color). Story 4-119 — 'media' is
  // the Media Tools tab. All are values on the same tab `SegmentedControl`
  // `ReportStudioInspector` (`ReportStudioPanels.tsx`, which owns
  // `ReportSettingsTab`'s own type) already renders — not a second
  // inspector mechanism.
  settingsTab: 'style' | 'content' | 'layout' | 'headerFooter' | 'media';
  presentation: ReportPresentation;
}

export interface ReportDesignerCanvasProps {
  schema: FormSchemaV2;
  previewMode: 'web' | 'print';
  // Story 4-113 — optional, additive canvas selection (see
  // `ReportRender.tsx`/`PagedReportPreview.tsx`'s own header comments for
  // why this reaches them as plain props, never editor-local state the
  // renderer reaches for itself).
  selectedSectionId?: string | null;
  onSelectSection?: (sectionId: string) => void;
  // Story 4-128d — a handle on the 'print' mode's ISOLATED document host. In
  // that mode the paper renders inside an iframe over the published report
  // artifact, so `TemplateEditorPage`'s outline-click navigation can no longer
  // reach the section element with `document.querySelector` and has to ask the
  // host to scroll it instead. Null in 'web' mode (nothing is isolated there),
  // which is exactly the caller's signal to use its own same-document scroll.
  printHostRef?: Ref<ReportPrintHostHandle>;
}

const CONTENT_LABELS: Record<keyof ReportContentToggles, string> = {
  footer: 'Footer',
  tableOfContents: 'Table of contents',
  // Story 4-110 — kept only so this `Record` stays total over
  // `keyof ReportContentToggles` (the wire field is retired-but-not-dropped
  // — see that type's own comment); no `toggleRow` call site renders this
  // key anymore.
  pageBreak: 'Page break',
  summary: 'Summary',
  flaggedItems: 'Flagged items',
  actions: 'Actions',
  disclaimer: 'Disclaimer',
  inspectionResults: 'Inspection results',
  tables: 'Tables',
  mediaSummary: 'Media summary',
  scores: 'Scores',
  // Story 4-117
  methodology: 'Methodology',
  showQuestionLabels: 'Question labels',
  showAnswers: 'Answers',
  showDescriptions: 'Question descriptions',
  metadata: 'Report metadata (author, location)',
  includeInternalNotes: 'Internal notes',
  evidenceCaptions: 'Evidence captions',
};

// Story 4-117 — "group findings by section/severity/status/assignee":
// severity is the only real dimension (see `ReportFindingsGroupBy`'s own
// `formSchema.d.ts` comment) — the other three are disabled with a stated
// reason, same pattern `4-116`'s `SectionPresentationTools` already
// established for its own unrealized values.
const FINDINGS_GROUP_BY_OPTIONS: Array<{ value: FindingsGroupBy; label: string }> = [
  { value: 'severity', label: 'Severity' },
  { value: 'section', label: 'Section' },
  { value: 'status', label: 'Status' },
  { value: 'assignee', label: 'Assignee' },
];

// Story 4-117 — "configure unanswered-value presentation": the REPORT-WIDE
// default a section's own `showEmpty` (4-116) falls back to when it has none
// set — see `ReportLayout.defaultShowEmpty`'s own comment for the full
// "global default + section-level override" reading.
const DEFAULT_SHOW_EMPTY_OPTIONS: Array<{ value: ShowEmptyMode; label: string }> = [
  { value: 'show', label: 'Show' },
  { value: 'hide', label: 'Hide' },
  { value: 'notProvided', label: '"Not provided"' },
];

// Story 4-84 — the font control's real, curated list (`REPORT_FONTS`, now
// 'sans'|'serif'|'mono'). Labels/preview classes are keyed off that SAME
// array (not re-listed by hand) so a future additive entry can't silently
// render label-less. `FONT_PREVIEW_CLASS` mirrors `ReportRender.tsx`'s own
// `FONT_CLASS` map exactly — 'sans' is this app's unstyled default, 'serif'/
// 'mono' step onto Tailwind's own (unmodified) `font-serif`/`font-mono`
// utilities, not a new webfont.
const FONT_LABELS: Record<ReportFont, string> = {
  sans: 'Sans-serif',
  serif: 'Serif',
  mono: 'Monospace',
};
const FONT_PREVIEW_CLASS: Record<ReportFont, string> = {
  sans: '',
  serif: 'font-serif',
  mono: 'font-mono',
};

// Story 4-85 — the two new decoration tokens, same enum-token pattern as
// `REPORT_FONTS`/`FONT_LABELS` above (curated list iterated so a future
// additive entry can't render label-less).
const UNDERLINE_WIDTH_LABELS: Record<SectionUnderlineWidth, string> = {
  full: 'Full width',
  text: 'Text width',
};
const SURFACE_RADIUS_LABELS: Record<SurfaceRadius, string> = {
  rounded: 'Rounded',
  sharp: 'Sharp',
};

// Story 4-88b — 4-88a's flat enable-list is replaced by the active-slot grid
// picker (`SlotGridPicker`) + relocate control (`DirectionalPad`), both new
// DS primitives (galleried at `/app/design` before use here, per DOD rule 7)
// composed below. `LETTERHEAD_CONTENT_KIND` is the one remaining per-key
// distinction the content editor needs: which input(s) a slot's `content`
// takes, mirroring `contracts/reports.js#ReportLetterhead`'s own comment
// (logos carry a URL/data-URI, title/addressBlock are position-only — the
// TEXT they show is the render-model's own title/subjectAddress field, not a
// second copy — footerPageNumber/footerText carry free text).
const LETTERHEAD_CONTENT_KIND: Record<LetterheadSlotKey, 'logo' | 'position-only' | 'text'> = {
  logoLeft: 'logo',
  logoCenter: 'logo',
  logoRight: 'logo',
  title: 'position-only',
  addressBlock: 'position-only',
  footerPageNumber: 'text',
  footerText: 'text',
};
const LETTERHEAD_CONTENT_PLACEHOLDER: Partial<Record<LetterheadSlotKey, string>> = {
  footerPageNumber: 'Page {page} of {total}',
  footerText: 'Footer text',
};

// Story 4-120 — Style Tools' new allowlisted-token label maps, same curated-
// list-iteration pattern every label map above already establishes.
const CAPTION_SCALE_LABELS: Record<TypographyCaptionScale, string> = { sm: 'Compact', md: 'Larger' };
const COLOR_ROLE_BRAND_LABELS: Record<ColorRoleBrand, string> = { accent: 'Accent color', neutral: 'Neutral (no tint)' };
const COLOR_ROLE_HEADING_LABELS: Record<ColorRoleHeading, string> = { default: 'Default ink', brand: 'Brand-tinted' };
const COLOR_ROLE_BORDER_LABELS: Record<ColorRoleBorder, string> = { default: 'Default', brand: 'Brand-tinted' };
const COLOR_ROLE_FINDING_SEVERITY_LABELS: Record<ColorRoleFindingSeverity, string> = {
  standard: 'Standard palette',
  brand: 'Brand-tinted',
};
const BORDER_DENSITY_LABELS: Record<BorderDensity, string> = { standard: 'Standard', bold: 'Bold' };
const COVER_VARIANT_LABELS: Record<CoverVariant, string> = { standard: 'Standard', accent: 'Accent band' };
const LETTERHEAD_VARIANT_LABELS: Record<LetterheadVariant, string> = { standard: 'Standard', divider: 'Divider rule' };
const TABLE_VARIANT_LABELS: Record<TableVariant, string> = { standard: 'Standard', striped: 'Striped rows' };
const FINDING_VARIANT_LABELS: Record<FindingVariant, string> = { standard: 'Standard', compact: 'Compact' };

/**
 * Story 4-120 — the shared destructive-reset confirmation shape "Reset
 * page"/"Reset report" both use (`4-113`'s own interaction contract:
 * "destructive reset actions require confirmation and show their scope").
 * Composes the EXISTING `Modal`/`ModalHeader`/`ModalBody`/`ModalFooter`/
 * `DialogActions` primitives (the same pattern every other destructive
 * confirmation in this app already uses, e.g. `TypeActivationDialog.tsx`) —
 * not a new primitive, just this feature's own composition of them, so no
 * `/app/design` gallery entry is owed here. `description` states the EXACT
 * scope in plain language; the reset itself is a normal typed command
 * (`onConfirm`), dispatched only after this explicit confirm step, so it is
 * fully undo-tracked like every other Report Studio mutation.
 */
export function ResetConfirmDialog({
  title,
  description,
  onConfirm,
  onClose,
}: {
  title: string;
  description: string;
  onConfirm: () => void;
  onClose: () => void;
}): JSX.Element {
  return (
    <Modal onClose={onClose} label={title} disableOutsideDismiss>
      <ModalHeader title={title} onClose={onClose} />
      <ModalBody>
        <p className="text-sm text-muted-foreground">{description}</p>
      </ModalBody>
      <ModalFooter>
        <DialogActions
          actions={[
            { id: 'cancel', label: 'Cancel', variant: 'secondary', onClick: onClose },
            {
              id: 'confirm',
              label: 'Reset',
              variant: 'destructive',
              onClick: () => {
                onConfirm();
                onClose();
              },
            },
          ]}
        />
      </ModalFooter>
    </Modal>
  );
}

export function ReportDesignerSettings({
  schema,
  dispatch,
  settingsTab,
  presentation,
}: ReportDesignerSettingsProps): JSX.Element {
  const [savedStyles, setSavedStyles] = useState<SavedStyle[]>([]);
  const [styleName, setStyleName] = useState('');
  const [styleNotice, setStyleNotice] = useState<string | null>(null);
  // Story 4-88b — which letterhead cell is currently selected in the
  // `SlotGridPicker` below. UI-local selection state, not persisted (unlike
  // every OTHER piece of state on this tab) — "which slot is active" is a
  // picker concern, not part of the saved `ReportLayout`.
  const [activeLetterheadSlot, setActiveLetterheadSlot] = useState<LetterheadSlotKey | null>(null);
  // Story 4-120 — which destructive reset confirmation (if any) is open.
  // `null` (default) — no dialog. UI-local, same "not part of the saved
  // ReportLayout" shape `activeLetterheadSlot` above already has.
  const [resetConfirm, setResetConfirm] = useState<'report' | null>(null);
  const layout = useMemo(() => reportLayoutOf(schema), [schema]);

  useEffect(() => {
    void listReportStyles().then(setSavedStyles);
  }, []);

  // Story 4-120 — "Organization brand preset": read-only, gated on the SAME
  // `org:branding:read` permission `OrgBrandingPage.tsx`'s own editor
  // requires — a principal without it (most non-admin roles) simply never
  // sees the "Apply organization brand" action below, rather than hitting a
  // 403 from an unauthorized fetch.
  const { user } = useAuth();
  const { hasPermission } = useCapabilities();
  const canReadOrgBranding = hasPermission('org:branding:read');
  const orgId = user?.organizationId ?? '';
  const { data: orgBranding } = useQuery({
    queryKey: orgBrandingKeys.detail(orgId),
    queryFn: () => getOrgBranding(orgId).then((r) => r.data),
    enabled: canReadOrgBranding && Boolean(orgId),
  });

  // Story 4-113 — the ONE place this component builds a
  // `patchReportLayout` command. Every control below calls this (or
  // `patchLetterheadSlot`, which itself calls this) instead of touching
  // `schema.report` directly — "UI controls dispatch typed commands rather
  // than mutating nested objects directly" (this story's own done-when),
  // applied to the pre-existing Style/Content panel rather than a rewrite
  // of its controls. `label` is a short, human-readable description of
  // the edit for the command's own undo metadata (`Undo ${label}` — see
  // `reportStudioCommands.ts`).
  const patchReport = (patch: Partial<ReportLayout>, label = 'Edit report style') =>
    dispatch({ type: 'patchReportLayout', label, payload: patch });

  // Story 4-88b — patch ONE letterhead slot's `{enabled, content}` through
  // the SAME `patchReport` round-trip every other field on this tab uses
  // (Save style/Apply a saved style covers letterhead for free, no second
  // persistence mechanism).
  const patchLetterheadSlot = (key: LetterheadSlotKey, patch: Partial<{ enabled: boolean; content: string | null }>) =>
    patchReport({ letterhead: { ...layout.letterhead, [key]: { ...layout.letterhead[key], ...patch } } });

  // Relocate the active slot's content to the adjacent named slot in
  // `direction` — a SWAP (`relocateLetterheadSlot`), never a one-way
  // overwrite, so whatever the target slot held is not silently destroyed.
  // Active selection FOLLOWS the content to its new position, so repeated
  // presses in one direction keep acting on the same content the user is
  // moving (mirrors `OrderedListField`'s "focus follows the moved row").
  const moveLetterheadSlot = (direction: DirectionalPadDirection) => {
    if (!activeLetterheadSlot) return;
    const target = findAdjacentSlot(LETTERHEAD_GRID_CELLS, activeLetterheadSlot, direction) as LetterheadSlotKey | null;
    if (!target) return;
    patchReport({ letterhead: relocateLetterheadSlot(layout.letterhead, activeLetterheadSlot, target) });
    setActiveLetterheadSlot(target);
  };

  const letterheadDirectionDisabled = (['up', 'down', 'left', 'right'] as DirectionalPadDirection[]).reduce(
    (acc, direction) => {
      acc[direction] =
        !activeLetterheadSlot || findAdjacentSlot(LETTERHEAD_GRID_CELLS, activeLetterheadSlot, direction) === null;
      return acc;
    },
    {} as Record<DirectionalPadDirection, boolean>,
  );

  const toggleRow = (key: keyof ReportContentToggles) => (
    <Cluster key={key} justify="between" align="center" gap={3} className="py-1.5">
      <span className="text-sm text-foreground">{CONTENT_LABELS[key]}</span>
      <Switch
        value={layout.content[key]}
        size="sm"
        aria-label={CONTENT_LABELS[key]}
        onChange={(next) => patchReport({ content: { ...layout.content, [key]: next } })}
      />
    </Cluster>
  );

  return (
    <Surface
      as="section"
      variant="bare"
      className="h-full min-h-0 overflow-y-auto"
      data-testid="template-report-inspector"
    >
      <Surface as="section" variant="flat" padding="sm" className="rounded-none border-x-0 border-t-0">
        <ContextHeader
          title={
            settingsTab === 'style'
              ? 'Report style'
              : settingsTab === 'layout'
                ? 'Document layout'
                : settingsTab === 'headerFooter'
                  ? 'Header & footer'
                  : settingsTab === 'media'
                    ? 'Media'
                    : 'Report content'
          }
          headingLevel={2}
        />
      </Surface>
      <Stack gap={4} className="min-w-0 p-4">
        {settingsTab === 'style' ? (
          <div className="space-y-5 text-sm">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Saved styles</p>
              <Select
                size="sm"
                className="mt-1 w-full"
                value={presentation.stylePresetId ?? ''}
                disabled={savedStyles.length === 0}
                onChange={(e) => {
                  const style = savedStyles.find((s) => s.name === e.target.value);
                  if (style?.layout) {
                    patchReport(style.layout as Partial<ReportLayout>, `Apply style "${style.name}"`);
                    // Story 4-113 — `ReportPresentation.stylePresetId` is the
                    // one field this schema already has a real caller for:
                    // applying a saved style is exactly "the current config
                    // now corresponds to this named preset."
                    dispatch({
                      type: 'setStylePreset',
                      label: `Apply style "${style.name}"`,
                      payload: { stylePresetId: style.name },
                    });
                    setStyleNotice(`Applied "${style.name}"`);
                  }
                }}
              >
                <option value="">{savedStyles.length > 0 ? 'Apply a saved style…' : 'No saved styles yet'}</option>
                {savedStyles.map((s) => (
                  <option key={s.name} value={s.name}>{s.name} ({s.pageSize ?? 'A4'})</option>
                ))}
              </Select>
              <Cluster gap={2} align="center" className="mt-2">
                <Input
                  size="sm"
                  className="min-w-0 flex-1"
                  placeholder="Style name…"
                  value={styleName}
                  onChange={(e) => setStyleName(e.target.value)}
                />
                <ToolbarActions
                  size="sm"
                  maxVisible={1}
                  actions={[{
                    id: 'save-report-style',
                    label: 'Save style',
                    variant: 'secondary',
                    disabled: !styleName.trim(),
                    onClick: async () => {
                      if (!styleName.trim()) return;
                      const name = styleName.trim();
                      await saveReportStyle(name, layout);
                      setSavedStyles(await listReportStyles());
                      // The just-saved preset now IS the current config —
                      // same `setStylePreset` command "Apply a saved style"
                      // dispatches above, tagged with this action's own label.
                      dispatch({ type: 'setStylePreset', label: `Save style "${name}"`, payload: { stylePresetId: name } });
                      setStyleNotice(`Saved "${name}"`);
                      setStyleName('');
                    },
                  }]}
                />
              </Cluster>
              {styleNotice && <p className="mt-1 text-xs text-primary">{styleNotice}</p>}
            </div>

            {/* Story 4-120 — "Organization brand preset": pulls the org's
                ALREADY-CONFIGURED branding (accent color, logo, font —
                `OrgBrandingPage.tsx`/`orgBranding.service.js`) into THIS
                template's Style-tab fields via the SAME `patchReport` round-
                trip every other control here uses, rather than a second,
                parallel brand-config system. A one-time copy, not a live
                binding — applying it again after an org-branding change
                re-copies the new values; the template does not "watch" the
                org's branding. Hidden entirely (not disabled) for a
                principal without `org:branding:read` — the same permission
                the org branding EDITOR itself requires — since there is
                nothing to preview/apply without it. */}
            {canReadOrgBranding && orgBranding && (orgBranding.accentColor || orgBranding.logoUrl || orgBranding.font) && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Organization brand</p>
                <p className="text-label text-muted-foreground">
                  Copy the organization&apos;s configured accent color{orgBranding.logoUrl ? ', logo' : ''} and font into
                  this report style.
                </p>
                <ToolbarActions
                  size="sm"
                  className="mt-2"
                  maxVisible={1}
                  actions={[{
                    id: 'apply-org-brand',
                    label: 'Apply organization brand',
                    variant: 'secondary',
                    onClick: () => {
                      const font: ReportFont =
                        orgBranding.font === 'serif' ? 'serif' : orgBranding.font === 'outfit' ? 'sans' : layout.font;
                      patchReport(
                        {
                          ...(orgBranding.accentColor ? { accentColor: orgBranding.accentColor } : {}),
                          ...(orgBranding.logoUrl ? { logoUrl: orgBranding.logoUrl } : {}),
                          font,
                        },
                        'Apply organization brand',
                      );
                      setStyleNotice('Applied organization brand');
                    },
                  }]}
                />
              </div>
            )}

            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Logo</p>
              <FileButton
                variant="secondary"
                className="mt-1 h-20 w-full overflow-hidden border-dashed text-xs text-muted-foreground"
                accept="image/*"
                inputLabel="Upload report logo"
                onSelect={(file) => {
                  const reader = new FileReader();
                  reader.onload = () => patchReport({ logoUrl: String(reader.result) });
                  reader.readAsDataURL(file);
                }}
              >
                {layout.logoUrl
                  ? <img src={layout.logoUrl} alt="logo" className="max-h-16 max-w-full object-contain" />
                  : <span className="flex items-center gap-1"><PhotoIcon className="h-4 w-4" /> Upload logo</span>}
              </FileButton>
              {layout.logoUrl && (
                <ToolbarActions
                  size="sm"
                  className="mt-1"
                  maxVisible={1}
                  actions={[{
                    id: 'remove-report-logo',
                    label: 'Remove logo',
                    variant: 'ghost',
                    onClick: () => patchReport({ logoUrl: '' }),
                  }]}
                />
              )}
            </div>

            {/* Story 4-114 — page size moved to the new "Layout" (Document
                Tools) tab, alongside the new orientation/margin-preset
                controls it now belongs with — see that tab's own render
                branch below. Not duplicated here. */}

            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Thumbnail size</p>
              <SegmentedControl
                aria-label="Report thumbnail size"
                value={layout.thumbnailSize}
                size="sm"
                onChange={(thumbnailSize) => patchReport({ thumbnailSize })}
                options={(['small', 'medium', 'large'] as const).map((size) => ({
                  value: size,
                  label: size[0]?.toUpperCase() + size.slice(1),
                }))}
              />
            </div>

            {/* ── Story 4-128f, defect D-4 — "Photo resolution" RETIRED ──────
                A `standard`/`high` radio pair used to sit here. It was
                persisted on `schema.report.photoResolution`, settable by the
                agent, and read by NOTHING: it never reached `ReportContent`
                and neither renderer referenced it. P06 §4.3 requires every
                shipped Report Studio control to survive this migration; a
                control that has never had a render effect is the one case the
                defect ledger itself calls out as honestly retirable.

                Not wired instead of retired, deliberately. "High resolution"
                promises a SECOND rendition of each photo — and there is only
                one: `photo.thumbnailUrl`, written once at capture time. Making
                the control real means an image-rendition pipeline with its own
                storage, checksums and asset descriptors, which is P06 §4.3's
                *Fonts and assets* plane and story 4-129's, not a renderer
                setting to be invented inside a retirement story. The control
                directly above — "Thumbnail size" — is the real, working answer
                to "how big do photos print".

                The FIELD stays on the wire (additive migrations only; a saved
                template keeps its value and `DEFAULT_REPORT_LAYOUT` still
                declares it). What is gone is the promise. */}

            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Accent color</p>
              <Cluster gap={2} align="center" className="mt-1">
                <ColorInput
                  value={layout.accentColor}
                  aria-label="Accent color"
                  title="Accent color"
                  onChange={(event) => patchReport({ accentColor: event.target.value })}
                />
                <span className="font-mono text-xs text-muted-foreground">{layout.accentColor}</span>
              </Cluster>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Font</p>
              <div className="mt-1">
                <RadioGroup
                  name="reportFont"
                  value={layout.font}
                  onChange={(e) => patchReport({ font: e.target.value as ReportFont })}
                  size="sm"
                  options={REPORT_FONTS.map((f) => ({
                    value: f,
                    label: FONT_LABELS[f],
                    // A live sample IN the actual face, not just a text label —
                    // this is what makes it a real typography choice rather
                    // than a name in a list (done-when: "the Style tab offers
                    // real font choices").
                    content: (
                      <span className={`block text-sm text-foreground ${FONT_PREVIEW_CLASS[f]}`}>
                        Aa Bb Cc 123
                      </span>
                    ),
                  }))}
                />
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Section headers</p>
              <RadioGroup
                name="headerStyle"
                value={layout.headerStyle}
                size="sm"
                onChange={(event) => patchReport({
                  headerStyle: event.target.value as 'filled' | 'centered' | 'minimal',
                })}
                options={[
                  { value: 'filled', label: 'Filled bar' },
                  { value: 'centered', label: 'Centered heading' },
                  { value: 'minimal', label: 'Minimal underline' },
                ]}
              />
            </div>

            {/* Story 4-85 — two decoration tokens, same `patchReport` round-trip
                as every control here (Saved styles covers them for free) and
                the same `RadioGroup` DS primitive 4-84's font control adopted
                (already a11y-passed and galleried, just reused here). */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Section underline width</p>
              <div className="mt-1">
                <RadioGroup
                  name="sectionUnderlineWidth"
                  value={layout.sectionUnderlineWidth}
                  onChange={(e) => patchReport({ sectionUnderlineWidth: e.target.value as SectionUnderlineWidth })}
                  size="sm"
                  horizontal
                  options={SECTION_UNDERLINE_WIDTHS.map((w) => ({
                    value: w,
                    label: UNDERLINE_WIDTH_LABELS[w],
                  }))}
                />
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Corner style</p>
              <div className="mt-1">
                <RadioGroup
                  name="surfaceRadius"
                  value={layout.surfaceRadius}
                  onChange={(e) => patchReport({ surfaceRadius: e.target.value as SurfaceRadius })}
                  size="sm"
                  horizontal
                  options={SURFACE_RADII.map((r) => ({
                    value: r,
                    label: SURFACE_RADIUS_LABELS[r],
                    // A live swatch IN that corner treatment — same "show,
                    // don't just name" reasoning as the font control's live
                    // sample above.
                    content: (
                      <span
                        aria-hidden="true"
                        className={`mt-1 block h-3 w-7 border border-primary bg-primary/20 ${
                          r === 'sharp' ? 'rounded-none' : 'rounded-md'
                        }`}
                      />
                    ),
                  }))}
                />
              </div>
            </div>

            {/* Story 4-82 — three new style dimensions, same `patchReport`
                round-trip every existing control above already uses (so
                "Save style"/"Apply a saved style" covers them for free —
                no second persistence mechanism). */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Heading size</p>
              <SegmentedControl
                aria-label="Report heading size"
                value={layout.headingScale}
                size="sm"
                onChange={(headingScale) => patchReport({ headingScale })}
                options={[
                  { value: 'sm', label: 'S' },
                  { value: 'md', label: 'M' },
                  { value: 'lg', label: 'L' },
                ]}
              />
            </div>

            {/* Story 4-114 — cover page on/off moved to the new "Layout"
                (Document Tools) tab, alongside its physical page controls.
                Not duplicated here. */}

            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Accent-tinted secondary text</p>
                <p className="text-label text-muted-foreground">
                  Tints the template caption with a contrast-safe shade of the accent color (print/PDF only).
                </p>
              </div>
              <Switch
                value={layout.secondaryTextFromAccent}
                onChange={(next) => patchReport({ secondaryTextFromAccent: next })}
                size="sm"
                aria-label="Accent-tinted secondary text"
              />
            </div>

            {/* ── Story 4-128f, defect D-4 — the report-level "Footer text"
                RETIRED ───────────────────────────────────────────────────
                A free-text Input used to sit here, bound to
                `schema.report.footerText`. No renderer has ever read it: the
                footer a reader actually sees comes from
                `letterhead.footerText` — a DIFFERENT field with the same name,
                edited in the Letterhead panel, which since 4-128e renders as a
                real CSS Paged Media margin box on every page of both hosts.

                Two identically-named controls, one of which silently does
                nothing, is worse than one. This is the one that does nothing,
                so this is the one that goes. The agent's `footer_text`
                parameter now writes the letterhead slot instead of this field
                (see `agents/src/capabilities/draft.js`), so the import
                style-hint path keeps working and starts producing a real
                footer. The field itself stays on the wire, unchanged. */}

            {/* Story 4-120 — Style Tools: semantic typography ROLES, colour
                ROLES, a border/surface density preset, and cover/letterhead/
                table/finding VARIANTS. Every control is a bounded,
                allowlisted enum — never a raw CSS/pixel/hex value — resolving
                through `agents/shared/formSchema.js`'s own guarded merge
                (`mergeTypographyRoles`/`mergeColorRoles`/`mergeVariants`) and
                both renderers' matching lookup (`ReportRender.tsx
                #resolveColorRoles` / `pdfExport.service.js`'s twin). Same
                `patchReport` round-trip as every control above — Save
                style/Apply a saved style covers these four fields for free. */}
            <div className="space-y-5 border-t border-border/40 pt-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Style Tools</p>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Caption text size</p>
                <p className="text-label text-muted-foreground">
                  Question labels, photo captions and footer text — heading size is set above.
                </p>
                <SegmentedControl<TypographyCaptionScale>
                  aria-label="Caption text size"
                  value={layout.typographyRoles.caption}
                  size="sm"
                  className="mt-1"
                  onChange={(caption) => patchReport({ typographyRoles: { ...layout.typographyRoles, caption } }, 'Change caption text size')}
                  options={TYPOGRAPHY_CAPTION_SCALES.map((value) => ({ value, label: CAPTION_SCALE_LABELS[value] }))}
                />
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Brand color role</p>
                <p className="text-label text-muted-foreground">
                  &quot;Neutral&quot; turns off accent tinting everywhere in this report at once.
                </p>
                <SegmentedControl<ColorRoleBrand>
                  aria-label="Brand color role"
                  value={layout.colorRoles.brand}
                  size="sm"
                  className="mt-1"
                  onChange={(brand) => patchReport({ colorRoles: { ...layout.colorRoles, brand } }, 'Change brand color role')}
                  options={COLOR_ROLE_BRAND_VALUES.map((value) => ({ value, label: COLOR_ROLE_BRAND_LABELS[value] }))}
                />
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Heading color role</p>
                <SegmentedControl<ColorRoleHeading>
                  aria-label="Heading color role"
                  value={layout.colorRoles.heading}
                  size="sm"
                  className="mt-1"
                  onChange={(heading) => patchReport({ colorRoles: { ...layout.colorRoles, heading } }, 'Change heading color role')}
                  options={COLOR_ROLE_HEADING_VALUES.map((value) => ({ value, label: COLOR_ROLE_HEADING_LABELS[value] }))}
                />
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Border color role</p>
                <SegmentedControl<ColorRoleBorder>
                  aria-label="Border color role"
                  value={layout.colorRoles.border}
                  size="sm"
                  className="mt-1"
                  onChange={(border) => patchReport({ colorRoles: { ...layout.colorRoles, border } }, 'Change border color role')}
                  options={COLOR_ROLE_BORDER_VALUES.map((value) => ({ value, label: COLOR_ROLE_BORDER_LABELS[value] }))}
                />
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Finding severity color role</p>
                <SegmentedControl<ColorRoleFindingSeverity>
                  aria-label="Finding severity color role"
                  value={layout.colorRoles.findingSeverity}
                  size="sm"
                  className="mt-1"
                  onChange={(findingSeverity) =>
                    patchReport({ colorRoles: { ...layout.colorRoles, findingSeverity } }, 'Change finding severity color role')
                  }
                  options={COLOR_ROLE_FINDING_SEVERITY_VALUES.map((value) => ({
                    value,
                    label: COLOR_ROLE_FINDING_SEVERITY_LABELS[value],
                  }))}
                />
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Border density</p>
                <SegmentedControl<BorderDensity>
                  aria-label="Border density"
                  value={layout.borderDensity}
                  size="sm"
                  className="mt-1"
                  onChange={(borderDensity) => patchReport({ borderDensity }, 'Change border density')}
                  options={BORDER_DENSITIES.map((value) => ({ value, label: BORDER_DENSITY_LABELS[value] }))}
                />
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cover page variant</p>
                <SegmentedControl<CoverVariant>
                  aria-label="Cover page variant"
                  value={layout.variants.cover}
                  size="sm"
                  className="mt-1"
                  onChange={(cover) => patchReport({ variants: { ...layout.variants, cover } }, 'Change cover page variant')}
                  options={COVER_VARIANTS.map((value) => ({ value, label: COVER_VARIANT_LABELS[value] }))}
                />
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Letterhead variant</p>
                <SegmentedControl<LetterheadVariant>
                  aria-label="Letterhead variant"
                  value={layout.variants.letterhead}
                  size="sm"
                  className="mt-1"
                  onChange={(letterhead) => patchReport({ variants: { ...layout.variants, letterhead } }, 'Change letterhead variant')}
                  options={LETTERHEAD_VARIANTS.map((value) => ({ value, label: LETTERHEAD_VARIANT_LABELS[value] }))}
                />
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Table variant</p>
                <SegmentedControl<TableVariant>
                  aria-label="Table variant"
                  value={layout.variants.table}
                  size="sm"
                  className="mt-1"
                  onChange={(table) => patchReport({ variants: { ...layout.variants, table } }, 'Change table variant')}
                  options={TABLE_VARIANTS.map((value) => ({ value, label: TABLE_VARIANT_LABELS[value] }))}
                />
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Finding variant</p>
                <SegmentedControl<FindingVariant>
                  aria-label="Finding variant"
                  value={layout.variants.finding}
                  size="sm"
                  className="mt-1"
                  onChange={(finding) => patchReport({ variants: { ...layout.variants, finding } }, 'Change finding variant')}
                  options={FINDING_VARIANTS.map((value) => ({ value, label: FINDING_VARIANT_LABELS[value] }))}
                />
              </div>
            </div>

            {/* Story 4-120 — "Reset report": scope is EVERYTHING
                `patchReportLayout` can reach (Style/Layout/Header-Footer/
                Content/Media) PLUS every section's own report-output fields
                (presentation/hidden/page-flow) — never capture
                fields/answers/template structure. Confirmed before dispatch
                per 4-113's own interaction contract; the reset itself is a
                normal undo-tracked command (`resetReport`). */}
            <div className="border-t border-border/40 pt-4">
              <ToolbarActions
                size="sm"
                maxVisible={1}
                actions={[{
                  id: 'reset-report',
                  label: 'Reset report…',
                  variant: 'destructive',
                  onClick: () => setResetConfirm('report'),
                }]}
              />
            </div>
            {resetConfirm === 'report' && (
              <ResetConfirmDialog
                title="Reset the whole report style?"
                description="Resets every Style, Layout, Header/Footer, Content and Media Tools setting back to its default, AND every section's own hide/page-flow/presentation overrides. Capture questions, answers and template structure are never affected. You can undo this afterwards."
                onClose={() => setResetConfirm(null)}
                onConfirm={() => dispatch({ type: 'resetReport', label: 'Reset report', payload: {} })}
              />
            )}
          </div>
        ) : settingsTab === 'layout' ? (
          <DocumentLayoutTools layout={layout} patchReport={patchReport} dispatch={dispatch} />
        ) : settingsTab === 'headerFooter' ? (
          <HeaderFooterTools
            layout={layout}
            patchReport={patchReport}
            patchLetterheadSlot={patchLetterheadSlot}
            activeLetterheadSlot={activeLetterheadSlot}
            setActiveLetterheadSlot={setActiveLetterheadSlot}
            moveLetterheadSlot={moveLetterheadSlot}
            letterheadDirectionDisabled={letterheadDirectionDisabled}
          />
        ) : settingsTab === 'media' ? (
          <MediaSummaryTools layout={layout} patchReport={patchReport} />
        ) : (
          <div>
            {/* Story 4-110 — `pageBreak` dropped from this list (not from
                `CONTENT_LABELS`/the contract — see `ReportContentToggles`'
                own comment for why the WIRE field stays). It no longer does
                anything (`ReportContentSection.pageBreak`, a real per-section
                control in `EditorCanvas.tsx`'s own section header row,
                replaces it) — leaving a checkbox here that silently stopped
                working would be exactly the "confusing second gate" this
                story's own done-when forbids.
                Story 4-114 — `tableOfContents` also dropped from this list
                (moved to the "Layout" Document Tools tab).
                Story 4-127 — `footer` dropped too, and with it the whole
                "Layout (PDF & Word)" heading this list used to sit under (it
                was the only entry left): the toggle never gated anything real
                (`.report-footer` in the backend PDF was unconditional, the
                web renderer had no footer element at all) and is now fully
                retired — the letterhead `footerText`/`footerPageNumber`
                slots (the `headerFooter` tab above) are the one real footer
                mechanism. Same "wire field stays, UI moves/disappears"
                treatment as `pageBreak`/`tableOfContents` above; `CONTENT_
                LABELS` keeps the key so its `Record` stays total. */}
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Inspection details</p>
            <div className="mt-1 divide-y divide-border/40">
              {(['summary', 'flaggedItems', 'actions', 'disclaimer', 'inspectionResults', 'tables', 'mediaSummary', 'scores'] as const).map(toggleRow)}
            </div>

            {/* Story 4-117 — Content Tools. Extends this SAME, pre-existing
                Content tab (not a second, parallel panel) — "Content Tools
                panel goes in ReportStudioPanels.tsx/ReportStudioInspector",
                and the document-selection branch of that inspector mounts
                THIS component unchanged; these are report-WIDE controls, the
                same kind `summary`/`flaggedItems`/etc. above already are, so
                they belong on the SAME tab, not a new one. */}
            <div className="mt-4 space-y-4 border-t border-border/40 pt-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Static blocks</p>
                <div className="mt-1 divide-y divide-border/40">
                  {layout.content.disclaimer && (
                    <div className="py-1.5">
                      <Textarea
                        size="sm"
                        rows={2}
                        placeholder="Disclaimer text shown at the end of the report"
                        aria-label="Disclaimer text"
                        value={layout.staticBlocks.disclaimer}
                        onChange={(e) =>
                          patchReport({ staticBlocks: { ...layout.staticBlocks, disclaimer: e.target.value } }, 'Edit disclaimer text')
                        }
                      />
                    </div>
                  )}
                  {toggleRow('methodology')}
                  {layout.content.methodology && (
                    <div className="py-1.5">
                      <Textarea
                        size="sm"
                        rows={2}
                        placeholder="Methodology text shown near the start of the report"
                        aria-label="Methodology text"
                        value={layout.staticBlocks.methodology}
                        onChange={(e) =>
                          patchReport({ staticBlocks: { ...layout.staticBlocks, methodology: e.target.value } }, 'Edit methodology text')
                        }
                      />
                    </div>
                  )}
                </div>
                <p className="mt-1 text-caption text-muted-foreground">
                  &quot;Introduction&quot; is set per report version (the Title/Summary fields when a report is created), not
                  here — there is already a real mechanism for it.
                </p>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Question display</p>
                <div className="mt-1 divide-y divide-border/40">
                  {(['showQuestionLabels', 'showAnswers', 'showDescriptions'] as const).map(toggleRow)}
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Unanswered questions</p>
                <SegmentedControl<ShowEmptyMode>
                  aria-label="Unanswered-question presentation"
                  value={layout.defaultShowEmpty}
                  size="sm"
                  className="mt-1"
                  onChange={(next) => patchReport({ defaultShowEmpty: next }, 'Change unanswered-question presentation')}
                  options={DEFAULT_SHOW_EMPTY_OPTIONS}
                />
                <p className="mt-1 text-caption text-muted-foreground">
                  The report-wide default; a section&apos;s own Presentation panel can override it.
                </p>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Metadata &amp; notes</p>
                <div className="mt-1 divide-y divide-border/40">
                  {(['metadata', 'includeInternalNotes'] as const).map(toggleRow)}
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Evidence</p>
                <div className="mt-1 divide-y divide-border/40">{(['evidenceCaptions'] as const).map(toggleRow)}</div>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Group findings by</p>
                <SegmentedControl<FindingsGroupBy>
                  aria-label="Group findings by"
                  value={layout.findingsGroupBy}
                  size="sm"
                  className="mt-1"
                  onChange={(next) => patchReport({ findingsGroupBy: next }, 'Change findings grouping')}
                  options={FINDINGS_GROUP_BY_OPTIONS.map((option) =>
                    option.value === 'severity'
                      ? option
                      : {
                          ...option,
                          disabled: true,
                          'aria-label': `${option.label} — disabled because findings carry no ${option.value} reference in this report's data model yet`,
                        },
                  )}
                />
                <p className="mt-1 text-caption text-muted-foreground">
                  Only Severity has real data behind it today — findings aren&apos;t linked to a section, every report
                  finding is already approval-filtered to one status, and a finding has no individual assignee.
                </p>
              </div>
            </div>
          </div>
        )}
      </Stack>
    </Surface>
  );
}

// Story 4-114 — Document Tools' report-wide (non-section) controls: page
// format (moved here from the Style tab), the two genuinely NEW fields
// (orientation, margin preset) and cover page / table of contents (moved
// here from Style/Content respectively) — the story's own "Add/configure
// cover page; add table of contents; configure page format and orientation;
// configure page margins from safe presets" bullets, all in the one panel
// their shared "physical page" nature actually belongs in. Every control
// dispatches through the SAME `patchReport` (`patchReportLayout` command)
// every other control on this settings panel already uses — no new command
// type needed, since `ReportLayout` already carries these two new fields.
function DocumentLayoutTools({
  layout,
  patchReport,
  dispatch,
}: {
  layout: ReportLayout;
  patchReport: (patch: Partial<ReportLayout>, label?: string) => void;
  dispatch: (command: ReportStudioCommand) => void;
}): JSX.Element {
  const letterheadMode = layout.letterhead.mode;
  // Story 4-120 — "Reset page": scope is the physical page format + the
  // repeating header/footer/letterhead (this tab's own fields, plus
  // `content.tableOfContents` and the Header/Footer tab's `letterhead` —
  // never Style/Content/Media Tools or any section's own settings). See
  // `reportStudioCommands.ts#resetPageChrome`'s own comment for the exact
  // field list.
  const [resetPageConfirm, setResetPageConfirm] = useState(false);
  return (
    <div className="space-y-5 text-sm">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Page size</p>
        <RadioGroup
          name="pageSize"
          value={layout.pageSize}
          size="sm"
          horizontal
          onChange={(event) => patchReport({ pageSize: event.target.value as 'A4' | 'letter' }, 'Change page size')}
          options={[
            { value: 'A4', label: 'A4' },
            { value: 'letter', label: 'US Letter' },
          ]}
        />
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Orientation</p>
        <SegmentedControl
          aria-label="Page orientation"
          value={layout.pageOrientation}
          size="sm"
          onChange={(pageOrientation) => patchReport({ pageOrientation: pageOrientation as PageOrientation }, 'Change page orientation')}
          options={PAGE_ORIENTATIONS.map((value) => ({ value, label: value === 'portrait' ? 'Portrait' : 'Landscape' }))}
        />
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Margins</p>
        <p className="text-label text-muted-foreground">Safe presets — an option that would leave too little printable space is disabled.</p>
        <SegmentedControl
          aria-label="Page margins"
          value={layout.marginsPreset}
          size="sm"
          className="mt-1"
          onChange={(marginsPreset) => patchReport({ marginsPreset: marginsPreset as MarginsPreset }, 'Change page margins')}
          options={MARGIN_PRESETS.map((preset) => {
            const safe = marginsPresetIsSafe(preset, {
              pageSize: layout.pageSize,
              orientation: layout.pageOrientation,
              letterheadMode,
            });
            return {
              value: preset,
              label: preset.charAt(0).toUpperCase() + preset.slice(1),
              disabled: !safe,
              // `exactOptionalPropertyTypes` — omit rather than set `undefined`.
              ...(safe
                ? {}
                : {
                    'aria-label': `${preset} margins — not enough printable space with the current page size, orientation and letterhead`,
                  }),
            };
          })}
        />
        {!marginsPresetIsSafe(layout.marginsPreset, { pageSize: layout.pageSize, orientation: layout.pageOrientation, letterheadMode }) && (
          <Callout tone="warning" className="mt-2" title="This margin leaves very little printable space">
            The current page size, orientation and letterhead reserve too little printable space for the selected
            margin. Choose a wider margin, portrait orientation, or a smaller page-format combination.
          </Callout>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cover page</p>
          <p className="text-label text-muted-foreground">A front-matter page before the report content.</p>
        </div>
        <Switch value={layout.coverPage} onChange={(next) => patchReport({ coverPage: next }, 'Toggle cover page')} size="sm" aria-label="Cover page" />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Table of contents</p>
          <p className="text-label text-muted-foreground">Lists every report section with its real page number.</p>
        </div>
        <Switch
          value={layout.content.tableOfContents}
          onChange={(next) => patchReport({ content: { ...layout.content, tableOfContents: next } }, 'Toggle table of contents')}
          size="sm"
          aria-label="Table of contents"
        />
      </div>

      {/* Story 4-120 — "Reset page": confirmed before dispatch per 4-113's
          own interaction contract; the reset itself is a normal undo-tracked
          command (`resetPageChrome`). */}
      <div className="border-t border-border/40 pt-4">
        <ToolbarActions
          size="sm"
          maxVisible={1}
          actions={[{
            id: 'reset-page',
            label: 'Reset page…',
            variant: 'destructive',
            onClick: () => setResetPageConfirm(true),
          }]}
        />
      </div>
      {resetPageConfirm && (
        <ResetConfirmDialog
          title="Reset page format and header/footer?"
          description="Resets page size, orientation, margins, cover page, table of contents and the repeating letterhead header/footer back to their defaults. Style colors/typography, Content Tools and every section's own settings are unaffected. You can undo this afterwards."
          onClose={() => setResetPageConfirm(false)}
          onConfirm={() => dispatch({ type: 'resetPageChrome', label: 'Reset page', payload: {} })}
        />
      )}
    </div>
  );
}

// Story 4-115 — WCAG AA contrast (4.5:1) of a candidate letterhead text color
// against a plain WHITE page, purely for INSTANT client-side feedback as a
// template author picks a color — the same math
// `backend/src/services/letterheadContrast.js#evaluateLetterheadTextColor`
// runs at save time (the REAL enforcement gate), hand-duplicated here rather
// than shared, matching this codebase's established "one small twin per real
// surface" convention (see that backend module's own header comment). A
// client-side pre-check only warns; it can never be the enforcement itself,
// since a user could always hit Save with dev tools disabling JS or a stale
// bundle — the backend gate is what actually protects the data.
function hexToRgbLocal(hex: string): [number, number, number] | null {
  const cleaned = hex.replace('#', '').trim();
  const full = cleaned.length === 3 ? cleaned.split('').map((c) => c + c).join('') : cleaned;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)];
}
function relativeLuminanceLocal([r, g, b]: [number, number, number]): number {
  const f = (c: number) => {
    const cn = c / 255;
    return cn <= 0.03928 ? cn / 12.92 : ((cn + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}
function whiteContrastRatio(hex: string): number | null {
  const rgb = hexToRgbLocal(hex);
  if (!rgb) return null;
  const lum = relativeLuminanceLocal(rgb);
  return (1 + 0.05) / (lum + 0.05);
}

const LETTERHEAD_LAYOUT_LABELS: Record<LetterheadLayout, string> = {
  standard: 'Standard',
  compact: 'Compact',
};

// Story 4-115 — recognized footer placeholder tokens, resolved by
// `pdfExport.service.js#buildFooterTemplate` (the downloaded PDF's real
// per-page footer — see `footerText`/`footerPageNumber`'s own contract
// comment for why this has no live-web-preview equivalent). Shown as help
// text under the slot editor rather than crammed into the `Input`'s
// `placeholder` attribute, which stays a short usage EXAMPLE.
const FOOTER_PLACEHOLDER_HINT =
  'Placeholders: {site} {status} {inspectionDate} {generatedAt} {reportReference} {confidential}';
const PAGE_NUMBER_PLACEHOLDER_HINT = `{page} {total} — plus every footer placeholder above`;

/**
 * Story 4-115 — Header/Footer Tools. Everything 4-88a/4-88b already shipped
 * (the slot grid picker, directional relocate, mode) moves here UNCHANGED
 * from the old Style tab (this tab is the "predefined left/centre/right
 * slots" home the story text asks for) plus this story's own genuinely new
 * controls: layout density, a typed confidentiality marking, an optional
 * text-color override (contrast-checked at save), and a fallback-state
 * preview so a missing logo / long address is visible in the editor before
 * publish without needing real broken data.
 *
 * Every control here dispatches through the SAME generic `patchReport`
 * (`patchReportLayout` command) every pre-existing letterhead control
 * already used — no new command TYPE was added for these three scalar
 * fields: they are plain siblings of `letterhead.mode` on `ReportLayout`,
 * already covered by that command's existing computed-inverse undo (the
 * whole `letterhead` object is captured before the patch and restored on
 * undo), the identical mechanism `mode`/slot `content`/`enabled` already
 * rely on. New command types were the right call for `4-114`'s
 * `hidden`/`pageBreak`/`reportOrder` because those live on
 * `schema.sections` (a different part of state `patchReportLayout` cannot
 * reach) — these fields live on `schema.report.letterhead`, exactly where
 * the existing generic command already operates.
 */
function HeaderFooterTools({
  layout,
  patchReport,
  patchLetterheadSlot,
  activeLetterheadSlot,
  setActiveLetterheadSlot,
  moveLetterheadSlot,
  letterheadDirectionDisabled,
}: {
  layout: ReportLayout;
  patchReport: (patch: Partial<ReportLayout>, label?: string) => void;
  patchLetterheadSlot: (key: LetterheadSlotKey, patch: Partial<{ enabled: boolean; content: string | null }>) => void;
  activeLetterheadSlot: LetterheadSlotKey | null;
  setActiveLetterheadSlot: (key: LetterheadSlotKey | null) => void;
  moveLetterheadSlot: (direction: DirectionalPadDirection) => void;
  letterheadDirectionDisabled: Record<DirectionalPadDirection, boolean>;
}): JSX.Element {
  const letterhead = layout.letterhead;
  const textColorRatio = letterhead.textColor ? whiteContrastRatio(letterhead.textColor) : null;
  const textColorFailsContrast = textColorRatio !== null && textColorRatio < 4.5;
  // Story 4-115 edge case — "preview missing-logo and long-address
  // fallbacks" WITHOUT needing real broken data: a self-contained swatch
  // reusing the REAL fallback components (`ReportLogoImage`'s own
  // `onError`, the same title/address text treatment the letterhead renders
  // with) fed deliberately-invalid/oversized sample data — not a new
  // rendering mechanism, a preview of the existing one.
  const LONG_SAMPLE_ADDRESS =
    'Unit 42B, The Waterside Commercial and Residential Complex, 1847 Old Pacific Highway, ' +
    'Wattlebank-on-the-Hill, Regional District 3847, Long-Format Testing Municipality';

  return (
    <div className="space-y-5 text-sm">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Repeat on</p>
        <p className="text-label text-muted-foreground">
          Which content pages show the letterhead (story 4-107's page-visibility policy).
        </p>
        <div className="mt-2">
          <RadioGroup
            name="letterheadMode"
            value={letterhead.mode}
            size="sm"
            horizontal
            onChange={(event) => patchReport({ letterhead: { ...letterhead, mode: event.target.value as typeof letterhead.mode } }, 'Change letterhead visibility')}
            options={[
              { value: 'firstContentPage', label: 'First page only' },
              { value: 'everyContentPage', label: 'Every page' },
              { value: 'none', label: 'Off' },
            ]}
          />
        </div>
      </div>

      {/* Story 4-115 — "configure compact and standard letterhead layouts". */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Layout density</p>
        <p className="text-label text-muted-foreground">Compact shrinks the logo/title footprint for tighter pages.</p>
        <SegmentedControl
          aria-label="Letterhead layout density"
          value={letterhead.layout}
          size="sm"
          className="mt-2"
          onChange={(value) => patchReport({ letterhead: { ...letterhead, layout: value as LetterheadLayout } }, 'Change letterhead layout density')}
          options={LETTERHEAD_LAYOUTS.map((value) => ({ value, label: LETTERHEAD_LAYOUT_LABELS[value] }))}
        />
      </div>

      <div className="border-t border-border pt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Letterhead slots
        </p>
        <p className="mt-1 text-label text-muted-foreground">
          Select a slot, then relocate it with the directional control.
        </p>
        <div className="mt-3 flex flex-wrap items-start gap-4">
          <SlotGridPicker
            aria-label="Letterhead slots"
            cells={LETTERHEAD_GRID_CELLS}
            rows={LETTERHEAD_GRID_ROWS}
            cols={LETTERHEAD_GRID_COLS}
            activeId={activeLetterheadSlot}
            onActivate={(id) => setActiveLetterheadSlot(id as LetterheadSlotKey)}
            renderCellBadge={(cell) =>
              letterhead[cell.id as LetterheadSlotKey]?.enabled ? (
                <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-primary" />
              ) : null
            }
          />
          <DirectionalPad
            contextLabel={activeLetterheadSlot ? LETTERHEAD_SLOT_LABELS[activeLetterheadSlot] : 'a slot'}
            disabled={letterheadDirectionDisabled}
            onMove={moveLetterheadSlot}
          />
        </div>

        {activeLetterheadSlot ? (
          (() => {
            const key = activeLetterheadSlot;
            const slot = letterhead[key];
            const kind = LETTERHEAD_CONTENT_KIND[key];
            return (
              <div className="mt-3 space-y-2 rounded-lg border border-border p-3">
                <Switch
                  value={slot.enabled}
                  onChange={(next) => patchLetterheadSlot(key, { enabled: next })}
                  size="sm"
                  label={LETTERHEAD_SLOT_LABELS[key]}
                />
                {kind === 'logo' && (
                  <div>
                    <FileButton
                      variant="secondary"
                      className="h-16 w-full overflow-hidden border-dashed text-xs text-muted-foreground"
                      accept="image/*"
                      inputLabel={`Upload ${LETTERHEAD_SLOT_LABELS[key]} logo`}
                      onSelect={(file) => {
                        const reader = new FileReader();
                        reader.onload = () => patchLetterheadSlot(key, { content: String(reader.result) });
                        reader.readAsDataURL(file);
                      }}
                    >
                      {slot.content ? (
                        <img src={slot.content} alt="" className="max-h-12 max-w-full object-contain" />
                      ) : (
                        <span className="flex items-center gap-1"><PhotoIcon className="h-4 w-4" /> Upload logo</span>
                      )}
                    </FileButton>
                    {slot.content && (
                      <ToolbarActions
                        size="sm"
                        className="mt-1"
                        maxVisible={1}
                        actions={[{
                          id: `remove-${key}-logo`,
                          label: 'Remove logo',
                          variant: 'ghost',
                          onClick: () => patchLetterheadSlot(key, { content: null }),
                        }]}
                      />
                    )}
                  </div>
                )}
                {kind === 'text' && (
                  <div>
                    <Input
                      size="sm"
                      className="w-full"
                      placeholder={LETTERHEAD_CONTENT_PLACEHOLDER[key]}
                      value={slot.content ?? ''}
                      onChange={(e) => patchLetterheadSlot(key, { content: e.target.value })}
                    />
                    <p className="mt-1 text-label text-muted-foreground">
                      {key === 'footerPageNumber' ? PAGE_NUMBER_PLACEHOLDER_HINT : FOOTER_PLACEHOLDER_HINT}
                    </p>
                  </div>
                )}
                {kind === 'position-only' && (
                  <p className="text-label text-muted-foreground">
                    Shows the report&apos;s own {key === 'title' ? 'title' : 'subject address'} — no separate
                    text to enter here.
                  </p>
                )}
              </div>
            );
          })()
        ) : (
          <p className="mt-3 text-xs text-muted-foreground">Select a slot above to edit it.</p>
        )}
      </div>

      {/* Story 4-115 — "add ... confidentiality label". A bounded, typed
          enum (never free text) — becomes visible only where a template
          author opts it into a footer slot via the {confidential}
          placeholder above (setting this alone changes no render, same
          "position-only until wired to a slot" shape title/addressBlock
          already use). */}
      <div className="border-t border-border pt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Confidentiality label</p>
        <p className="text-label text-muted-foreground">
          Available as the <code>{'{confidential}'}</code> footer placeholder above.
        </p>
        <Select
          size="sm"
          className="mt-2 w-full"
          value={letterhead.confidentiality}
          onChange={(e) =>
            patchReport(
              { letterhead: { ...letterhead, confidentiality: e.target.value as LetterheadConfidentiality } },
              'Change confidentiality label',
            )
          }
        >
          {LETTERHEAD_CONFIDENTIALITY_LEVELS.map((value) => (
            <option key={value} value={value}>
              {value === 'none' ? 'None' : LETTERHEAD_CONFIDENTIALITY_LABELS[value]}
            </option>
          ))}
        </Select>
      </div>

      {/* Story 4-115 — optional letterhead text-color override, contrast-
          checked at save (backend/src/services/letterheadContrast.js). This
          client-side ratio is an early warning only; the real gate runs on
          save. */}
      <div className="border-t border-border pt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Text color</p>
        <p className="text-label text-muted-foreground">Overrides the title/address text color. Leave unset to use the default.</p>
        <Cluster gap={2} align="center" className="mt-2">
          <ColorInput
            value={letterhead.textColor ?? '#1f2937'}
            aria-label="Letterhead text color"
            title="Letterhead text color"
            onChange={(event) => patchReport({ letterhead: { ...letterhead, textColor: event.target.value } }, 'Change letterhead text color')}
          />
          <span className="font-mono text-xs text-muted-foreground">{letterhead.textColor ?? 'Default'}</span>
          {letterhead.textColor && (
            <ToolbarActions
              size="sm"
              maxVisible={1}
              actions={[{
                id: 'clear-letterhead-text-color',
                label: 'Use default',
                variant: 'ghost',
                onClick: () => patchReport({ letterhead: { ...letterhead, textColor: null } }, 'Reset letterhead text color'),
              }]}
            />
          )}
        </Cluster>
        {textColorFailsContrast && textColorRatio !== null && (
          <Callout tone="warning" className="mt-2" title="This color may fail contrast on save">
            {letterhead.textColor} measures {textColorRatio.toFixed(2)}:1 against the report&apos;s white page —
            WCAG AA needs at least 4.5:1. Saving will be rejected until a darker color is chosen.
          </Callout>
        )}
      </div>

      {/* Story 4-115 edge case — "missing-logo and long-address fallbacks are
          previewable in the editor before publish". Reuses the REAL fallback
          components (`ReportLogoImage`'s own `onError` text-identity
          fallback; the same title/address text treatment
          `ui/ReportLetterhead.tsx` renders with) fed deliberately-broken/
          oversized sample data — a preview of the existing mechanism, not a
          new one. */}
      <div className="border-t border-border pt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Preview fallback states</p>
        <p className="text-label text-muted-foreground">
          What a missing logo or an unusually long address looks like — no need to break a real logo URL to see it.
        </p>
        <div className="mt-3 space-y-3 rounded-lg border border-dashed border-border p-3">
          <div>
            <p className="text-label font-semibold text-muted-foreground">Missing logo</p>
            <ReportLogoImage
              src="https://example.invalid/broken-logo.png"
              // Story 4-128f: was `layout.footerText`, a field with no render
              // effect anywhere (defect D-4) that happened to be reachable
              // here. A fallback for a BROKEN LOGO wants a stable identity
              // string, not a retired style field.
              fallbackText="Inspection Report"
              className="max-h-10 max-w-[140px] object-contain"
            />
          </div>
          <div>
            <p className="text-label font-semibold text-muted-foreground">Long address</p>
            <p className="mt-0.5 text-center text-xs text-muted-foreground break-words">
              {LONG_SAMPLE_ADDRESS}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Story 4-119 — Media Tools: the real controls for the report-WIDE
// `ReportLayout.mediaSummary` object (unlike `SectionPresentationTools`'
// per-section controls in `ReportStudioPanels.tsx`, this is a document-level
// tool group, like `DocumentLayoutTools`/`HeaderFooterTools` above — hence
// it lives on its own settings tab, not the section inspector). Every
// control dispatches through the SAME `patchReport` (`patchReportLayout`
// command) every other control on this settings panel already uses — no new
// command type needed, since `mediaSummary` is just one more field on the
// same `ReportLayout` shape that command already patches (`4-118`'s own
// "reuse the generic command" precedent, applied here to `patchReportLayout`
// instead of `setSectionPresentationProperty`, since this is report-wide).
//
// Unsupported combinations are DISABLED with a stated reason, matching this
// codebase's established Report Studio norm:
//   * `groupBy: 'finding'` — a photo may be cited by zero, one, or several
//     approved findings (`inspection.findings[].linkedEvidenceIds`, many-to-
//     many); unlike `question`/`section` (each evidence has at most one
//     owning field/section), there is no single honest group a multiply-
//     cited photo belongs to. See `agents/shared/formSchema.js
//     #MEDIA_SUMMARY_GROUP_BY_VALUES`'s own comment for the full reasoning.
//   * `showLocation`/`showAnnotations` — no per-photo capture-time location
//     exists in this data model, and photo annotation data is legacy-only
//     (write path retired story 4-83b) — see `DEFAULT_REPORT_LAYOUT
//     .mediaSummary`'s own comment (`agents/shared/formSchema.js`).
const MEDIA_SUMMARY_LAYOUT_LABELS: Record<MediaSummaryLayout, string> = {
  grid: 'Grid',
  list: 'List',
  fullWidth: 'Full width',
  appendix: 'Appendix',
};
const MEDIA_SUMMARY_GROUP_BY_LABELS: Record<MediaSummaryGroupBy, string> = {
  none: 'None',
  question: 'Question',
  section: 'Section',
  finding: 'Finding',
};

function MediaSummaryTools({
  layout,
  patchReport,
}: {
  layout: ReportLayout;
  patchReport: (patch: Partial<ReportLayout>, label?: string) => void;
}): JSX.Element {
  const media = layout.mediaSummary;
  const patchMedia = (patch: Partial<ReportLayout['mediaSummary']>, label: string) =>
    patchReport({ mediaSummary: { ...media, ...patch } }, label);

  return (
    <div className="space-y-5 text-sm">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Layout</p>
        <p className="text-label text-muted-foreground">
          How the report-wide photo rollup at the end of the report is arranged. &quot;Appendix&quot; moves it to a
          separate, distinctly-labelled block at the very end of the document.
        </p>
        <SegmentedControl<MediaSummaryLayout>
          aria-label="Media summary layout"
          value={media.layout}
          size="sm"
          className="mt-1"
          onChange={(next) => patchMedia({ layout: next }, 'Change media summary layout')}
          options={MEDIA_SUMMARY_LAYOUTS.map((value) => ({ value, label: MEDIA_SUMMARY_LAYOUT_LABELS[value] }))}
        />
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Thumbnail density</p>
        <p className="text-label text-muted-foreground">
          Reuses the report&apos;s existing thumbnail size (Style tab) — a grid of larger thumbnails shows fewer per row.
        </p>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Image fit</p>
        <SegmentedControl<MediaSummaryImageFit>
          aria-label="Media image fit"
          value={media.imageFit}
          size="sm"
          className="mt-1"
          onChange={(next) => patchMedia({ imageFit: next }, 'Change media image fit')}
          options={MEDIA_SUMMARY_IMAGE_FITS.map((value) => ({
            value,
            label: value === 'cover' ? 'Cover (crop to fill)' : 'Contain (show whole image)',
          }))}
        />
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Photo details</p>
        <div className="mt-1 divide-y divide-border/40">
          <Cluster justify="between" align="center" gap={3} className="py-1.5">
            <span className="text-sm text-foreground">Captions</span>
            <Switch
              value={media.showCaptions}
              size="sm"
              aria-label="Show captions"
              onChange={(next) => patchMedia({ showCaptions: next }, 'Toggle media captions')}
            />
          </Cluster>
          <Cluster justify="between" align="center" gap={3} className="py-1.5">
            <span className="text-sm text-foreground">Timestamps</span>
            <Switch
              value={media.showTimestamps}
              size="sm"
              aria-label="Show timestamps"
              onChange={(next) => patchMedia({ showTimestamps: next }, 'Toggle media timestamps')}
            />
          </Cluster>
          <Cluster justify="between" align="center" gap={3} className="py-1.5">
            <span className="text-sm text-foreground">Location</span>
            <Switch
              value={false}
              disabled
              size="sm"
              aria-label="Show location — disabled, no per-photo capture location exists yet"
              onChange={() => {}}
            />
          </Cluster>
          <Cluster justify="between" align="center" gap={3} className="py-1.5">
            <span className="text-sm text-foreground">Annotations</span>
            <Switch
              value={false}
              disabled
              size="sm"
              aria-label="Show annotations — disabled, annotation data is legacy-only and no longer captured"
              onChange={() => {}}
            />
          </Cluster>
        </div>
        <p className="mt-1 text-caption text-muted-foreground">
          Location and annotations are not yet available: no per-photo capture location exists in this report&apos;s
          data model, and photo annotations were a legacy mechanism retired before this report pipeline existed.
        </p>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Group photos by</p>
        <SegmentedControl<MediaSummaryGroupBy>
          aria-label="Group media by"
          value={media.groupBy}
          size="sm"
          className="mt-1"
          onChange={(next) => patchMedia({ groupBy: next }, 'Change media grouping')}
          options={MEDIA_SUMMARY_GROUP_BY_VALUES.map((value) =>
            UNREALIZED_MEDIA_SUMMARY_GROUP_BY.has(value)
              ? {
                  value,
                  label: MEDIA_SUMMARY_GROUP_BY_LABELS[value],
                  disabled: true,
                  'aria-label': `${MEDIA_SUMMARY_GROUP_BY_LABELS[value]} — disabled because a photo may be cited by zero, one, or several findings, so there is no single group it honestly belongs to`,
                }
              : { value, label: MEDIA_SUMMARY_GROUP_BY_LABELS[value] },
          )}
        />
        <p className="mt-1 text-caption text-muted-foreground">
          Question and Section group on the photo&apos;s own resolved field/section; a photo with neither (e.g. a
          legacy room photo) falls into an &quot;Ungrouped&quot; group.
        </p>
      </div>
    </div>
  );
}

/** The report canvas is intentionally presentation-only; all layout changes
 * are owned by `ReportDesignerSettings` in the workspace inspector.
 * `selectedSectionId`/`onSelectSection` (story 4-113) are the one exception
 * to "presentation-only": a canvas click is a SELECTION, not a layout
 * mutation — it changes `ReportStudioOutline`'s/`ReportStudioInspector`'s
 * own selection state, never `schema`/`presentation` themselves. */
export function ReportDesignerCanvas({
  schema,
  previewMode,
  selectedSectionId = null,
  onSelectSection,
  printHostRef,
}: ReportDesignerCanvasProps): JSX.Element {
  const layout = useMemo(() => reportLayoutOf(schema), [schema]);
  const previewContent = useMemo(() => buildPreviewReportContent(schema, layout), [schema, layout]);

  return (
    <Stack gap={3} data-testid="template-report-canvas">
      <Surface as="section" variant="flat" padding="sm">
        <ContextHeader
          title="Report preview"
          headingLevel={2}
          action={<span className="text-caption capitalize text-muted-foreground">{previewMode}</span>}
        />
      </Surface>
      {/* Story 4-106 — a real page-proportioned wrapper, not a generic
          reading-width column. `ReportRender.tsx`'s own doc comment always
          described this exact contract ("callers that want a paper-width
          preview apply their own max-width wrapper keyed off
          `style.pageSize`") but no caller ever built it — a `Container
          width="reading"` (a fixed, pageSize-blind reading column) stood in
          its place, which is why a cover page rendered inside it read as an
          arbitrary small block rather than "a page." `reportPageWidthMm`
          (`components/ui/reportPageFormat.ts`) is the same single source
          `ReportCoverPage`'s own height reads, so the two can never drift
          onto different page proportions. `TokenBox`, not `Container`: the
          width is a caller-computed value keyed off template config, not
          one of `Container`'s closed named presets (P10 §4.4's own
          "caller-supplied value through a layout primitive" carve-out). */}
      {previewMode === 'print' ? (
        // Story 4-108 — real, page-fragmented pagination (Paged.js, behind
        // the `PagedReportPreview` adapter — see that component's own
        // header comment) replaces the single continuous `ReportRender`
        // block this mode rendered pre-4-108. Story 4-128d moved that
        // pagination into an ISOLATED DOCUMENT (an iframe over the published
        // report artifact); this branch, its wrapper and its props are
        // unchanged by that move, which is the point — the toggle, the
        // selection and the page-width cap below all behave exactly as they
        // did. No `Surface variant="flat"`
        // card here: each real page fragment now carries its own
        // page-shaped shadow/background (`reportPagedPreview.css`), so a
        // second, OUTER card around the whole stack would double up rather
        // than help. `TokenBox` still caps width at the template's own
        // `reportPageWidthMm` (matches this mode's pre-4-108 wrapper, and
        // Paged.js's own `@page size` — same source, see
        // `PagedReportPreview.tsx`'s stylesheet builder) so the stack still
        // shrinks responsively on a narrow viewport instead of overflowing.
        <TokenBox
          maxWidth={`${reportPageWidthMm(layout.pageSize, layout.pageOrientation)}mm`}
          className="mx-auto w-full"
          data-agent-target="form-preview"
        >
          <PagedReportPreview
            content={previewContent}
            selectedSectionKey={selectedSectionId}
            // `exactOptionalPropertyTypes` — an optional prop must be
            // OMITTED, not set to `undefined`, when there is no handler.
            {...(onSelectSection ? { onSelectSection } : {})}
            {...(printHostRef ? { hostRef: printHostRef } : {})}
          />
        </TokenBox>
      ) : (
        <TokenBox maxWidth={`${reportPageWidthMm(layout.pageSize)}mm`} className="mx-auto w-full">
          <Surface as="section" variant="bare" padding="none" data-agent-target="form-preview">
            <p className="text-right text-caption text-muted-foreground">
              Powered by <span className="font-bold text-foreground">DefectSnap</span>
            </p>
            <ReportRender
              content={previewContent}
              variant="screen"
              selectedSectionKey={selectedSectionId}
              {...(onSelectSection ? { onSelectSection } : {})}
            />
          </Surface>
        </TokenBox>
      )}
    </Stack>
  );
}
